const ENABLED_MIN_WIDTH = 700;
const MAX_AIRCRAFT = 4;
const SPAWN_EVERY_MS = 2600;
const SPEED_MULTIPLIER = 1.2;
const SPRITE_URL = new URL('holiday-304-us-aircraft-sprites-transparent.png', import.meta.url).href;
const FX_URL = new URL('holiday-304-aircraft-sprites-transparent.png', import.meta.url).href;

const aircraftSets = [
  {
    name: 'f-5-freedom-fighter',
    frame: { x: 58, y: 120, w: 642, h: 144 },
    scale: .12,
    minSpeed: 72,
    maxSpeed: 92,
  },
  {
    name: 'c-119-flying-boxcar',
    frame: { x: 778, y: 64, w: 666, h: 220 },
    scale: .13,
    minSpeed: 42,
    maxSpeed: 56,
  },
  {
    name: 'c-47-skytrain',
    frame: { x: 88, y: 442, w: 584, h: 196 },
    scale: .14,
    minSpeed: 42,
    maxSpeed: 58,
  },
  {
    name: 'c-130-hercules',
    frame: { x: 752, y: 420, w: 694, h: 214 },
    scale: .13,
    minSpeed: 40,
    maxSpeed: 54,
  },
  {
    name: 'uh-1-huey',
    frame: { x: 96, y: 750, w: 588, h: 184 },
    scale: .12,
    minSpeed: 38,
    maxSpeed: 52,
  },
  {
    name: 'ch-46-sea-knight',
    frame: { x: 790, y: 716, w: 610, h: 218 },
    scale: .13,
    minSpeed: 36,
    maxSpeed: 50,
  },
];

const explosionFrames = [
  { x: 510, y: 890, w: 96, h: 92 },
  { x: 676, y: 874, w: 126, h: 112 },
  { x: 858, y: 858, w: 154, h: 136 },
];

class HolidayFlyers {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'holiday-304-flyers';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.context = this.canvas.getContext('2d');
    this.image = new Image();
    this.image.src = SPRITE_URL;
    this.fxImage = new Image();
    this.fxImage.src = FX_URL;
    this.aircraft = [];
    this.explosions = [];
    this.lastTime = 0;
    this.lastSpawn = 0;
    this.running = false;

    this.resize = this.resize.bind(this);
    this.frame = this.frame.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);

    this.loadedImages = 0;
    const handleImageLoad = () => {
      this.loadedImages += 1;

      if (this.loadedImages === 2) {
        this.start();
      }
    };

    this.image.addEventListener('load', handleImageLoad, { once: true });
    this.fxImage.addEventListener('load', handleImageLoad, { once: true });
  }

  get enabled() {
    return window.innerWidth >= ENABLED_MIN_WIDTH && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start() {
    if (!this.enabled || this.running) {
      return;
    }

    document.body.append(this.canvas);
    this.resize();
    this.running = true;
    this.lastTime = performance.now();

    window.addEventListener('resize', this.resize, { passive: true });
    document.addEventListener('pointerdown', this.handlePointerDown, true);
    document.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibility);
    requestAnimationFrame(this.frame);
  }

  stop() {
    this.running = false;
    this.aircraft = [];
    this.explosions = [];
    this.canvas.remove();
    document.body.classList.remove('holiday-304-aiming');
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('pointerdown', this.handlePointerDown, true);
    document.removeEventListener('pointermove', this.handlePointerMove);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  resize() {
    if (!this.enabled) {
      this.stop();
      return;
    }

    const ratio = window.devicePixelRatio || 1;
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.canvas.width = Math.round(this.width * ratio);
    this.canvas.height = Math.round(this.height * ratio);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.context.imageSmoothingEnabled = false;
  }

  frame(now) {
    if (!this.running) {
      return;
    }

    if (document.hidden) {
      requestAnimationFrame(this.frame);
      return;
    }

    const delta = Math.min(now - this.lastTime, 48);
    this.lastTime = now;

    if (now - this.lastSpawn > SPAWN_EVERY_MS && this.aircraft.length < MAX_AIRCRAFT) {
      this.spawnAircraft();
      this.lastSpawn = now;
    }

    this.update(delta);
    this.draw();
    requestAnimationFrame(this.frame);
  }

  spawnAircraft() {
    const set = aircraftSets[Math.floor(Math.random() * aircraftSets.length)];
    const direction = Math.random() > .5 ? 1 : -1;
    const frame = set.frame;
    const width = frame.w * set.scale;
    const height = frame.h * set.scale;

    this.aircraft.push({
      set,
      direction,
      x: direction === 1 ? -width - 20 : this.width + 20,
      y: this.height * (.18 + Math.random() * .58),
      width,
      height,
      speed: (set.minSpeed + Math.random() * (set.maxSpeed - set.minSpeed)) * SPEED_MULTIPLIER * direction,
      bob: Math.random() * Math.PI * 2,
      hit: false,
    });
  }

  update(delta) {
    for (const item of this.aircraft) {
      item.x += item.speed * delta / 1000;
      item.bob += delta * .0012;
    }

    this.aircraft = this.aircraft.filter((item) => item.x > -item.width - 80 && item.x < this.width + 80 && !item.hit);

    for (const blast of this.explosions) {
      blast.age += delta;
    }

    this.explosions = this.explosions.filter((blast) => blast.age < 360);
  }

  draw() {
    this.context.clearRect(0, 0, this.width, this.height);

    for (const item of this.aircraft) {
      const frame = item.set.frame;
      const x = Math.round(item.x);
      const y = Math.round(item.y + Math.sin(item.bob) * 2);

      this.context.save();

      if (item.direction === 1) {
        this.context.translate(x + item.width, y);
        this.context.scale(-1, 1);
        this.context.drawImage(this.image, frame.x, frame.y, frame.w, frame.h, 0, 0, item.width, item.height);
      } else {
        this.context.drawImage(this.image, frame.x, frame.y, frame.w, frame.h, x, y, item.width, item.height);
      }

      this.context.restore();
    }

    for (const blast of this.explosions) {
      const frame = explosionFrames[Math.min(Math.floor(blast.age / 120), explosionFrames.length - 1)];
      const size = blast.size * (1 + blast.age / 720);
      this.context.globalAlpha = Math.max(0, 1 - blast.age / 420);
      this.context.drawImage(this.fxImage, frame.x, frame.y, frame.w, frame.h, blast.x - size / 2, blast.y - size / 2, size, size);
      this.context.globalAlpha = 1;
    }
  }

  handlePointerDown(event) {
    if (!this.running || event.pointerType === 'touch') {
      return;
    }

    const hit = this.findHit(event.clientX, event.clientY);

    if (!hit) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    hit.hit = true;
    this.explosions.push({
      x: event.clientX,
      y: event.clientY,
      age: 0,
      size: Math.max(48, hit.width * .72),
    });
    this.flash(event.clientX, event.clientY);
  }

  handlePointerMove(event) {
    if (!this.running || event.pointerType === 'touch') {
      return;
    }

    document.body.classList.toggle('holiday-304-aiming', !!this.findHit(event.clientX, event.clientY));
  }

  findHit(x, y) {
    return [...this.aircraft].reverse().find((item) => {
      const itemY = item.y + Math.sin(item.bob) * 6;
      const insetX = item.width * .12;
      const insetY = item.height * .18;

      return x >= item.x + insetX
        && x <= item.x + item.width - insetX
        && y >= itemY + insetY
        && y <= itemY + item.height - insetY;
    });
  }

  flash(x, y) {
    const flash = document.createElement('span');
    flash.className = 'holiday-304-hit-flash';
    flash.style.left = `${x}px`;
    flash.style.top = `${y}px`;
    document.body.append(flash);
    flash.addEventListener('animationend', () => flash.remove(), { once: true });
  }

  handleVisibility() {
    this.lastTime = performance.now();
  }
}

new HolidayFlyers();
